// src/tabs/messages/CancellationPoliciesEditor.jsx — #6 (ADR-206) + refonte VOIE A.
//
// Section « Politiques d'annulation » du sous-onglet Direct, refondue selon la
// maquette de référence (docs/handoff/maquettes/maquette-direct-fbz.html,
// section .policies → .policy + .pol-logs/.logchips) :
//   • UNE carte par politique (Flexible / Modérée / Stricte / Non remboursable) ;
//   • chaque carte = son texte FR éditable (flag « Éditable » ; « Défaut » sur la
//     non remboursable) + ses logements en CHIPS dedans (nom + × pour retirer) +
//     un bouton « + Ajouter » pour assigner un logement.
//
// Cette section FUSIONNE l'ancien éditeur des 4 textes ET l'ancienne grille
// d'affectation « Politique par logement » (composant CancellationPolicyAssignment
// SUPPRIMÉ) : l'assignation se fait désormais via les chips dans les cartes.
//
// LOGIQUE INCHANGÉE (présentation seule) :
//   • Édition du texte FR → action cancellation_policies_save (saveCancellationPolicy),
//     EN/ES existants PRÉSERVÉS (segment figé — jamais effacés côté serveur).
//   • Assignation / retrait d'un logement → action cancellation_policy_assignment_set
//     (setCancellationPolicyAssignment) sur logement_site_content.politique_annulation.
//     Retrait (×) = ré-assignation à la politique par défaut « non_remboursable ».
//
// Lot P — politiques CUSTOM : carte « + Nouvelle politique » (create, clé
// générée serveur custom_*), label + textes éditables, bouton « Supprimer »
// avec confirmation 2 temps (JAMAIS sur les 4 d'origine) ; 409 politique_affectee
// → « Réaffecte d'abord ces logements : … » (aucune réaffectation auto).
import { useState, useEffect, useRef } from "react";
import {
  ShieldCheck,
  AlertTriangle,
  Check,
  Pencil,
  Star,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { DS } from "../../lib/ui";
import { getAllLogsFlat } from "../../lib/sousloc-helpers";
import {
  fetchCancellationPolicies,
  saveCancellationPolicy,
  createCancellationPolicy,
  deleteCancellationPolicy,
} from "../../lib/cancellation-policies-api";
import {
  fetchCancellationPolicyAssignments,
  setCancellationPolicyAssignment,
} from "../../lib/cancellation-policy-assignment-api";

const LABELS = {
  flexible: "Flexible",
  moderee: "Modérée",
  stricte: "Stricte",
  non_remboursable: "Non remboursable",
};
const ORDER = ["flexible", "moderee", "stricte", "non_remboursable"];
const DEFAULT_KEY = "non_remboursable";

// Pastille de couleur par niveau d'engagement (cosmétique).
const LEVEL = {
  flexible: DS.success,
  moderee: DS.warning,
  stricte: DS.corail,
  non_remboursable: DS.danger,
};

export default function CancellationPoliciesEditor({ cfg }) {
  const [policies, setPolicies] = useState(null); // [{ key, default_fr, override }]
  const [assignments, setAssignments] = useState(null); // [{ logement_id, politique_annulation }]
  const [error, setError] = useState(null);

  const reload = async () => {
    setError(null);
    try {
      const [pol, asg] = await Promise.all([
        fetchCancellationPolicies(),
        fetchCancellationPolicyAssignments(),
      ]);
      setPolicies(pol.items || []);
      setAssignments(Array.isArray(asg.items) ? asg.items : []);
    } catch (e) {
      setError(e.message || "Erreur de chargement");
    }
  };
  useEffect(() => {
    reload();
  }, []);

  // Nom d'affichage par id immuable (jamais de lookup par nom).
  const nameById = new Map(getAllLogsFlat(cfg).map((l) => [l.id, l.nom]));

  // Mise à jour optimiste locale d'une affectation.
  const applyAssign = (logement_id, politique_annulation) => {
    setAssignments((prev) =>
      (prev || []).map((it) =>
        it.logement_id === logement_id ? { ...it, politique_annulation } : it,
      ),
    );
  };

  // Mise à jour optimiste locale d'une politique (texte et/ou label custom).
  const applyPolicyPatch = (key, patch) => {
    setPolicies((prev) =>
      (prev || []).map((it) => (it.key === key ? { ...it, ...patch } : it)),
    );
  };

  // Lot P — ajout local d'une politique custom fraîchement créée (clé serveur).
  const applyPolicyCreated = (key, policy) => {
    setPolicies((prev) => [
      ...(prev || []),
      {
        key,
        label: policy.label,
        override: { text_fr: policy.text_fr },
        is_custom: true,
      },
    ]);
  };

  // Lot P — retrait local d'une politique custom supprimée côté serveur.
  const applyPolicyDeleted = (key) => {
    setPolicies((prev) => (prev || []).filter((it) => it.key !== key));
  };

  const loading = (!policies || !assignments) && !error;
  const byKey = new Map((policies || []).map((p) => [p.key, p]));
  // Cartes : les 4 d'origine dans l'ordre figé, PUIS les customs (ordre blob).
  const orderedItems = [
    ...ORDER.map((k) => byKey.get(k)).filter(Boolean),
    ...(policies || []).filter((p) => p.is_custom),
  ];

  return (
    <div style={{ marginTop: 26 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 11,
          marginBottom: 5,
        }}
      >
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: DS.cielSoft,
            color: DS.marine,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <ShieldCheck size={18} strokeWidth={2} />
        </span>
        <h3
          style={{
            fontFamily: DS.fontDisplay,
            fontWeight: 600,
            fontSize: 19,
            color: DS.marineDark,
            margin: 0,
          }}
        >
          Politiques d'annulation
        </h3>
      </div>
      <p
        style={{
          color: DS.textSecondary,
          fontSize: 12.5,
          margin: "0 0 14px 45px",
          maxWidth: 820,
          lineHeight: 1.55,
        }}
      >
        Écrivez chaque politique et glissez-y les logements concernés. Un
        logement n'a qu'une politique à la fois. Le texte de sa politique est
        inséré dans le mail « Demande reçue », à l'endroit de l'info{" "}
        <strong>Politique d'annulation</strong>.
      </p>

      {/* Lot P — bandeau : ces textes partent dans un VRAI mail voyageur (E1). */}
      <div
        data-testid="cancellation-policies-warning"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          margin: "0 0 14px 45px",
          maxWidth: 820,
          background: DS.warningBg || "#fdf6e3",
          border: `1px solid ${DS.warning}`,
          borderRadius: 8,
          padding: "9px 12px",
          fontSize: 12.5,
          fontWeight: 600,
          color: DS.warningText || DS.marineDark,
          lineHeight: 1.5,
        }}
      >
        <AlertTriangle
          size={14}
          strokeWidth={2}
          style={{ flexShrink: 0, color: DS.warning }}
        />
        ⚠️ Ces textes partent dans le vrai mail “Demande reçue”
      </div>

      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            color: DS.dangerText,
            fontSize: 12,
            fontWeight: 600,
            margin: "0 0 12px 45px",
          }}
        >
          <AlertTriangle size={13} strokeWidth={2} /> {error}
        </div>
      )}
      {loading && (
        <div style={{ fontSize: 12, color: DS.textMuted, marginLeft: 45 }}>
          Chargement…
        </div>
      )}

      {policies && assignments && (
        <div
          data-testid="cancellation-policies"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 12,
          }}
        >
          {orderedItems.map((item) => {
            const key = item.key;
            const assigned = assignments
              .filter((a) => a.politique_annulation === key)
              .map((a) => ({
                id: a.logement_id,
                nom: nameById.get(a.logement_id) || a.logement_id,
              }));
            const available = assignments
              .filter((a) => a.politique_annulation !== key)
              .map((a) => ({
                id: a.logement_id,
                nom: nameById.get(a.logement_id) || a.logement_id,
              }));
            return (
              <PolicyCard
                key={key}
                item={item}
                isDefault={key === DEFAULT_KEY}
                assigned={assigned}
                available={available}
                nameById={nameById}
                onSaveText={applyPolicyPatch}
                onAssign={applyAssign}
                onDeleted={applyPolicyDeleted}
              />
            );
          })}
          {/* Lot P — carte de création d'une politique custom. */}
          <NewPolicyCard onCreated={applyPolicyCreated} />
        </div>
      )}
    </div>
  );
}

// ── Carte d'une politique ────────────────────────────────────────────────────
function PolicyCard({
  item,
  isDefault,
  assigned,
  available,
  nameById,
  onSaveText,
  onAssign,
  onDeleted,
}) {
  const key = item.key;
  const isCustom = !!item.is_custom;
  const currentFr =
    (item.override && item.override.text_fr) || item.default_fr || "";
  const currentLabel = item.label || "";
  const [draft, setDraft] = useState(currentFr);
  const [labelDraft, setLabelDraft] = useState(currentLabel);
  const [busy, setBusy] = useState(false);
  const [okFlash, setOkFlash] = useState(false);
  const [rowError, setRowError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Re-sync si le texte serveur change (anti stale-closure snapshot).
  useEffect(() => {
    setDraft(currentFr);
  }, [currentFr]);
  useEffect(() => {
    setLabelDraft(currentLabel);
  }, [currentLabel]);

  // Payload commun : FR + EN/ES existants PRÉSERVÉS (segment figé).
  const buildPayload = (textFr) => {
    const payload = { text_fr: textFr };
    const ov = item.override || {};
    if (ov.text_en && ov.text_en.trim()) payload.text_en = ov.text_en;
    if (ov.text_es && ov.text_es.trim()) payload.text_es = ov.text_es;
    return payload;
  };

  // Enregistre le FR au blur si modifié.
  const saveFr = async () => {
    const next = draft.trim();
    if (next === currentFr.trim()) return;
    if (!next) {
      setRowError("Le texte FR ne peut pas être vide.");
      return;
    }
    setBusy(true);
    setOkFlash(false);
    setRowError(null);
    try {
      const payload = buildPayload(draft);
      const r = await saveCancellationPolicy(key, payload);
      onSaveText(key, { override: r.override || payload });
      setOkFlash(true);
    } catch (e) {
      setRowError(e.message || "Enregistrement échoué");
    } finally {
      setBusy(false);
    }
  };

  // Lot P — enregistre le label (custom uniquement) au blur si modifié.
  const saveLabel = async () => {
    const next = labelDraft.trim();
    if (!isCustom || next === currentLabel.trim()) return;
    if (!next) {
      setRowError("Le nom ne peut pas être vide.");
      setLabelDraft(currentLabel);
      return;
    }
    setBusy(true);
    setOkFlash(false);
    setRowError(null);
    try {
      const payload = { ...buildPayload(currentFr), label: next };
      await saveCancellationPolicy(key, payload);
      onSaveText(key, { label: next });
      setOkFlash(true);
    } catch (e) {
      setRowError(e.message || "Enregistrement échoué");
      setLabelDraft(currentLabel);
    } finally {
      setBusy(false);
    }
  };

  // Lot P — suppression (custom uniquement, confirmation 2 temps déjà passée).
  // 409 politique_affectee → liste des logements à réaffecter d'abord (AUCUNE
  // réaffectation automatique — décision Dimitri).
  const doDelete = async () => {
    setBusy(true);
    setRowError(null);
    try {
      await deleteCancellationPolicy(key);
      onDeleted(key);
    } catch (e) {
      if (e.code === "politique_affectee") {
        const names = (e.logements || []).map(
          (id) => (nameById && nameById.get(id)) || id,
        );
        setRowError(`Réaffecte d'abord ces logements : ${names.join(", ")}`);
      } else {
        setRowError(e.message || "Suppression échouée");
      }
      setBusy(false);
    }
  };

  const dot = isCustom ? DS.marine : LEVEL[key] || DS.textMuted;

  return (
    <div
      data-testid={`policy-card-${key}`}
      style={{
        background: DS.card,
        border: `0.5px solid ${DS.border}`,
        borderRadius: DS.radiusCard,
        boxShadow: "0 8px 24px -14px rgba(20,51,80,.2)",
        padding: "15px 16px",
      }}
    >
      {/* En-tête : pastille + nom + flag */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 9,
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            background: dot,
            flexShrink: 0,
          }}
        />
        {isCustom ? (
          <input
            data-testid={`policy-label-${key}`}
            value={labelDraft}
            onChange={(e) => {
              setLabelDraft(e.target.value);
              setOkFlash(false);
              setRowError(null);
            }}
            onBlur={saveLabel}
            disabled={busy}
            maxLength={60}
            aria-label="Nom de la politique"
            style={{
              fontFamily: DS.fontDisplay,
              fontWeight: 600,
              fontSize: 15,
              color: DS.marineDark,
              flex: 1,
              minWidth: 0,
              border: `1px solid transparent`,
              borderBottom: `1px dashed ${DS.border}`,
              background: "transparent",
              padding: "2px 4px",
            }}
          />
        ) : (
          <span
            style={{
              fontFamily: DS.fontDisplay,
              fontWeight: 600,
              fontSize: 15,
              color: DS.marineDark,
              flex: 1,
            }}
          >
            {LABELS[key] || key}
          </span>
        )}
        {isDefault ? (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: DS.textMuted,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Star
              size={12}
              strokeWidth={2}
              fill={DS.warning}
              color={DS.warning}
            />{" "}
            Défaut
          </span>
        ) : (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: DS.textMuted,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Pencil size={12} strokeWidth={2} /> Éditable
          </span>
        )}
      </div>

      {/* Texte FR éditable (save au blur) */}
      <textarea
        data-testid={`policy-text-${key}`}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setOkFlash(false);
          setRowError(null);
        }}
        onBlur={saveFr}
        disabled={busy}
        rows={3}
        aria-label={`Texte de la politique ${LABELS[key] || item.label || key}`}
        style={{
          width: "100%",
          boxSizing: "border-box",
          fontSize: 12.5,
          color: DS.textSecondary,
          lineHeight: 1.5,
          background: "#fafbfc",
          border: `1px solid ${DS.border}`,
          borderRadius: 8,
          padding: "9px 11px",
          fontFamily: DS.fontBody,
          resize: "vertical",
          minHeight: 58,
        }}
      />
      {okFlash && !rowError && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            color: DS.success,
            fontSize: 11,
            fontWeight: 700,
            marginTop: 5,
          }}
        >
          <Check size={12} strokeWidth={2.4} /> Enregistré
        </div>
      )}
      {rowError && (
        <div
          style={{
            display: "flex",
            gap: 6,
            marginTop: 5,
            fontSize: 11,
            color: DS.dangerText,
            background: DS.dangerBg,
            border: `1px solid ${DS.danger}`,
            borderRadius: 6,
            padding: "6px 8px",
          }}
        >
          <AlertTriangle
            size={12}
            strokeWidth={2}
            style={{ flexShrink: 0, marginTop: 1 }}
          />{" "}
          {rowError}
        </div>
      )}

      {/* Logements assignés (chips) */}
      <div
        style={{
          marginTop: 11,
          paddingTop: 11,
          borderTop: `1px dashed ${DS.border}`,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            color: DS.textMuted,
            marginBottom: 7,
          }}
        >
          Logements
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            alignItems: "center",
          }}
        >
          {assigned.length === 0 && (
            <span
              style={{
                color: DS.textMuted,
                fontStyle: "italic",
                fontSize: 12,
                border: `1px dashed ${DS.border}`,
                borderRadius: 16,
                padding: "4px 12px",
              }}
            >
              Aucun logement
            </span>
          )}
          {assigned.map((l) => (
            <LogementChip
              key={l.id}
              logement={l}
              removable={!isDefault}
              onRemove={() => onRemove(l.id, onAssign, setRowError)}
            />
          ))}
          <AddLogementMenu
            logements={available}
            onPick={(logId) => onAssignTo(logId, key, onAssign, setRowError)}
          />
        </div>
      </div>

      {/* Lot P — suppression (customs uniquement ; JAMAIS sur les 4 d'origine).
          Pattern 2 temps rouge (cf. direct-msg-delete-confirmbox). */}
      {isCustom && (
        <div style={{ marginTop: 11 }}>
          {confirmDelete ? (
            <div
              data-testid={`policy-delete-confirmbox-${key}`}
              style={{
                border: `1px solid ${DS.danger}`,
                background: DS.dangerBg,
                borderRadius: 8,
                padding: "10px 12px",
              }}
            >
              <div
                style={{
                  fontSize: 12.5,
                  color: DS.dangerText,
                  fontWeight: 600,
                  display: "flex",
                  gap: 7,
                  alignItems: "flex-start",
                  lineHeight: 1.5,
                  marginBottom: 9,
                }}
              >
                <Trash2
                  size={14}
                  strokeWidth={2}
                  style={{ flexShrink: 0, marginTop: 1 }}
                />
                Ceci supprime définitivement cette politique. Continuer ?
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  data-testid={`policy-delete-confirm-${key}`}
                  disabled={busy}
                  onClick={() => {
                    setConfirmDelete(false);
                    doDelete();
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontFamily: DS.fontBody,
                    fontSize: 12.5,
                    fontWeight: 600,
                    padding: "7px 12px",
                    borderRadius: 8,
                    border: `1px solid ${DS.danger}`,
                    background: DS.danger,
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Oui, supprimer
                </button>
                <button
                  type="button"
                  data-testid={`policy-delete-cancel-${key}`}
                  onClick={() => setConfirmDelete(false)}
                  style={{
                    border: `1px solid ${DS.border}`,
                    background: DS.card,
                    color: DS.textSecondary,
                    fontSize: 12.5,
                    fontWeight: 600,
                    fontFamily: DS.fontBody,
                    padding: "7px 12px",
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              data-testid={`policy-delete-${key}`}
              disabled={busy}
              onClick={() => setConfirmDelete(true)}
              className="fbz-press"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontFamily: DS.fontBody,
                fontSize: 12,
                fontWeight: 600,
                padding: "6px 11px",
                borderRadius: 8,
                border: `1px solid ${DS.danger}`,
                background: DS.card,
                color: DS.dangerText,
                cursor: "pointer",
              }}
            >
              <Trash2 size={13} strokeWidth={2} /> Supprimer
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Lot P — carte « + Nouvelle politique » (form inline nom + texte FR) ──────
function NewPolicyCard({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [textFr, setTextFr] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const reset = () => {
    setOpen(false);
    setLabel("");
    setTextFr("");
    setErr(null);
  };

  const submit = async () => {
    const cleanLabel = label.trim();
    if (!cleanLabel) {
      setErr("Le nom est requis.");
      return;
    }
    if (!textFr.trim()) {
      setErr("Le texte FR est requis.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await createCancellationPolicy(cleanLabel, textFr);
      onCreated(r.key, r.policy || { label: cleanLabel, text_fr: textFr });
      reset();
    } catch (e) {
      setErr(e.message || "Création échouée");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid="policy-create-open"
        className="fbz-press"
        onClick={() => setOpen(true)}
        style={{
          border: `1.5px dashed ${DS.borderHover}`,
          borderRadius: DS.radiusCard,
          background: "transparent",
          color: DS.marine,
          fontFamily: DS.fontBody,
          fontSize: 13.5,
          fontWeight: 600,
          cursor: "pointer",
          minHeight: 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 7,
        }}
      >
        <Plus size={16} strokeWidth={2} /> Nouvelle politique
      </button>
    );
  }

  return (
    <div
      data-testid="policy-create-form"
      style={{
        background: DS.card,
        border: `0.5px solid ${DS.border}`,
        borderRadius: DS.radiusCard,
        boxShadow: "0 8px 24px -14px rgba(20,51,80,.2)",
        padding: "15px 16px",
      }}
    >
      <div
        style={{
          fontFamily: DS.fontDisplay,
          fontWeight: 600,
          fontSize: 15,
          color: DS.marineDark,
          marginBottom: 9,
        }}
      >
        Nouvelle politique
      </div>
      <input
        data-testid="policy-create-label"
        value={label}
        onChange={(e) => {
          setLabel(e.target.value);
          setErr(null);
        }}
        maxLength={60}
        placeholder="Nom (ex. Longs séjours)"
        aria-label="Nom de la nouvelle politique"
        style={{
          width: "100%",
          boxSizing: "border-box",
          fontSize: 13,
          color: DS.marineDark,
          background: "#fafbfc",
          border: `1px solid ${DS.border}`,
          borderRadius: 8,
          padding: "8px 11px",
          fontFamily: DS.fontBody,
          marginBottom: 8,
        }}
      />
      <textarea
        data-testid="policy-create-text"
        value={textFr}
        onChange={(e) => {
          setTextFr(e.target.value);
          setErr(null);
        }}
        rows={3}
        placeholder="Texte FR de la politique (part dans le mail « Demande reçue »)"
        aria-label="Texte FR de la nouvelle politique"
        style={{
          width: "100%",
          boxSizing: "border-box",
          fontSize: 12.5,
          color: DS.textSecondary,
          lineHeight: 1.5,
          background: "#fafbfc",
          border: `1px solid ${DS.border}`,
          borderRadius: 8,
          padding: "9px 11px",
          fontFamily: DS.fontBody,
          resize: "vertical",
          minHeight: 58,
        }}
      />
      {err && (
        <div
          style={{
            display: "flex",
            gap: 6,
            marginTop: 5,
            fontSize: 11,
            color: DS.dangerText,
            background: DS.dangerBg,
            border: `1px solid ${DS.danger}`,
            borderRadius: 6,
            padding: "6px 8px",
          }}
        >
          <AlertTriangle
            size={12}
            strokeWidth={2}
            style={{ flexShrink: 0, marginTop: 1 }}
          />{" "}
          {err}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button
          type="button"
          data-testid="policy-create-submit"
          disabled={busy}
          onClick={submit}
          className="fbz-press"
          style={{
            fontFamily: DS.fontBody,
            fontSize: 12.5,
            fontWeight: 600,
            padding: "8px 13px",
            borderRadius: 8,
            border: "1px solid transparent",
            background: DS.marine,
            color: "#fff",
            cursor: busy ? "not-allowed" : "pointer",
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? "Création…" : "Créer"}
        </button>
        <button
          type="button"
          data-testid="policy-create-cancel"
          onClick={reset}
          style={{
            border: `1px solid ${DS.border}`,
            background: DS.card,
            color: DS.textSecondary,
            fontSize: 12.5,
            fontWeight: 600,
            fontFamily: DS.fontBody,
            padding: "8px 13px",
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          Annuler
        </button>
      </div>
    </div>
  );
}

// Retrait d'un logement = ré-assignation à la politique par défaut.
async function onRemove(logementId, onAssign, setRowError) {
  try {
    const r = await setCancellationPolicyAssignment(logementId, DEFAULT_KEY);
    onAssign(logementId, r.politique_annulation || DEFAULT_KEY);
  } catch (e) {
    setRowError(assignErr(e));
  }
}

// Assignation d'un logement à cette politique.
async function onAssignTo(logementId, key, onAssign, setRowError) {
  try {
    const r = await setCancellationPolicyAssignment(logementId, key);
    onAssign(logementId, r.politique_annulation || key);
  } catch (e) {
    setRowError(assignErr(e));
  }
}

function assignErr(e) {
  return e.message === "logement_site_content_not_found"
    ? "Pas de fiche site pour ce logement (créez-la dans Fiche logement → Website)."
    : e.message || "Enregistrement échoué";
}

// ── Chip d'un logement assigné ───────────────────────────────────────────────
function LogementChip({ logement, removable, onRemove }) {
  return (
    <span
      data-testid={`logchip-${logement.id}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: DS.card,
        border: `1px solid ${DS.border}`,
        borderRadius: 16,
        padding: removable ? "4px 6px 4px 10px" : "4px 12px",
        fontSize: 12,
        fontWeight: 500,
        color: DS.marineDark,
      }}
    >
      {logement.nom}
      {removable && (
        <button
          type="button"
          aria-label={`Retirer ${logement.nom}`}
          onClick={onRemove}
          className="fbz-press"
          style={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            border: "none",
            background: DS.pageBg,
            color: DS.textMuted,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
        >
          <X size={11} strokeWidth={2.4} />
        </button>
      )}
    </span>
  );
}

// ── Bouton « + Ajouter » + menu de sélection d'un logement ───────────────────
function AddLogementMenu({ logements, onPick }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <span
      ref={wrapRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        type="button"
        className="fbz-press"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          border: `1px dashed ${DS.borderHover}`,
          borderRadius: 16,
          padding: "5px 11px",
          fontSize: 12,
          fontWeight: 600,
          color: DS.marine,
          cursor: "pointer",
          background: DS.card,
          fontFamily: DS.fontBody,
        }}
      >
        <Plus size={13} strokeWidth={2} /> Ajouter
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 30,
            background: DS.card,
            border: `0.5px solid ${DS.border}`,
            borderRadius: 12,
            boxShadow: "0 12px 30px -10px rgba(20,51,80,.28)",
            padding: 6,
            width: 240,
            maxWidth: "80vw",
            maxHeight: 260,
            overflowY: "auto",
          }}
        >
          {logements.length === 0 && (
            <div
              style={{ fontSize: 12, color: DS.textMuted, padding: "8px 10px" }}
            >
              Tous les logements sont déjà sur cette politique.
            </div>
          )}
          {logements.map((l) => (
            <button
              key={l.id}
              type="button"
              className="fbz-press"
              onClick={() => {
                onPick(l.id);
                setOpen(false);
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                border: "none",
                background: "transparent",
                borderRadius: 8,
                padding: "8px 10px",
                fontSize: 12.5,
                color: DS.marineDark,
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              {l.nom}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
